import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, CheckCircle2, AlertTriangle } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const IMG_PEPINIERE = "/images/Labour-2.JPG";
const IMG_RHIZOME_SECTION = "/images/Rhizome-4.webp";
const IMG_CHAMP = "/images/Champ-4.webp";

const etapes = [
  { num: "01", title: "Préparation du sol", desc: "Labour profond (25-30 cm) et hersage fin. Réaliser une analyse de sol si possible. pH idéal entre 5,5 et 7,5. Drainage correct indispensable — éviter les zones engorgées." },
  { num: "02", title: "Période de plantation", desc: "Planter de mars à mai lorsque le sol est réchauffé au-dessus de 8°C. Éviter les gelées tardives. La plantation précoce (mars) favorise une meilleure reprise et une croissance plus vigoureuse la première année." },
  { num: "03", title: "Mise en place des rhizomes", desc: "Densité recommandée : 1 rhizome tous les 70 cm (espacement 0,7m × 0,7m), soit environ 2 big bags par hectare. Planter à 5-10 cm de profondeur. Compacter légèrement après plantation. Nous mettons à disposition une planteuse à pomme de terre pour faciliter la mise en place." },
  { num: "04", title: "Entretien années 1-2", desc: "Le miscanthus est lent à s'établir les premières années. Désherbage conseillé la première saison pour limiter la concurrence. Irrigation conseillée surtout en cas de sécheresse prolongée. À partir de la 3ème année : culture totalement autonome." },
  { num: "05", title: "Première récolte", desc: "Récolte en février à partir de la 3e année. À maturité (an 4-5), le rendement atteint 10 à 25 tonnes de matière sèche par hectare selon les conditions climatiques et pédologiques." },
];

export default function LesRhizomes() {
  useSEO({
    title: "Rhizomes de miscanthus et guide de plantation | Miscanthus Sarthe",
    description: "Rhizomes de Miscanthus giganteus produits en Sarthe, disponibles de février à avril. Guide de plantation complet : densité, préparation du sol, entretien, récolte.",
    path: "/les-rhizomes",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="rhizomes-page" style={{ backgroundColor: "#f5f2ec" }}>
      {/* Header */}
      <div className="page-header" style={{ paddingTop: "7rem" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-4" />
          <h1 className="font-display text-5xl sm:text-6xl mb-5" style={{ color: "#3a2b24" }}>
            Les Rhizomes & la Plantation
          </h1>
          <p className="text-base max-w-2xl" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            Tout ce que vous devez savoir pour réussir votre plantation de miscanthus, de la préparation du sol à la première récolte.
          </p>
        </div>
      </div>

      {/* Image banner */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={IMG_PEPINIERE} alt="Pépinière de miscanthus - jeunes pousses" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(245,242,236,1) 0%, transparent 60%)" }} />
      </div>

      {/* Qu'est-ce qu'un rhizome */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl mb-6" style={{ color: "#3a2b24" }}>
                Qu'est-ce qu'un rhizome de miscanthus ?
              </h2>
              <div className="space-y-4 text-base leading-relaxed" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                <p>
                  Le rhizome est la tige souterraine de la plante, un organe de réserve qui permet au miscanthus de repartir chaque année. C'est à partir de ces rhizomes que se développe l'ensemble de la plante.
                </p>
                <p>
                  Nos rhizomes sont <strong>sélectionnés et conditionnés</strong> avec soin pour garantir un taux de reprise optimal. Chaque big bag contient <strong>10 000 rhizomes</strong> et couvre <strong>5 000 m²</strong>, soit 2 big bags pour planter 1 hectare.
                </p>
                <p>
                  La qualité des rhizomes est déterminante pour la réussite de la plantation. Nous vous fournissons des rhizomes issus de notre propre exploitation, garantissant leur fraîcheur et leur vitalité.
                </p>
                <p>
                  Le miscanthus est une plante <strong>non invasive</strong> : si les rhizomes s'étendent progressivement en touffe, la floraison automnale ne produit pas de graines fertiles et n'entraîne donc aucune dispersion ni germination spontanée dans l'environnement.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <img
                src={IMG_RHIZOME_SECTION}
                alt="Rhizome de miscanthus"
                className="rounded-2xl w-full object-cover shadow-md"
                style={{ aspectRatio: "4/3" }}
              />
              <div className="rounded-xl p-6" style={{ backgroundColor: "#eae4d8", border: "1px solid #d8d1c4" }}>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar size={24} style={{ color: "#b57a3e" }} />
                  <h3 className="font-display text-xl" style={{ color: "#3a2b24" }}>Disponibilité</h3>
                </div>
                <p className="text-sm mb-4" style={{ color: "#3a2b24", fontFamily: "'Satoshi', sans-serif" }}>
                  Les rhizomes sont disponibles uniquement en <strong>période de plantation</strong> :
                </p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {["Février", "Mars", "Avril"].map((m) => (
                    <div key={m} className="month-available text-center py-2 rounded-md text-sm font-body font-500">
                      {m}
                    </div>
                  ))}
                </div>
                <div className="text-xs p-3 rounded-md" style={{ backgroundColor: "rgba(181,122,62,0.1)", color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>
                  <AlertTriangle size={12} className="inline mr-1" />
                  Commande anticipée recommandée avant fin janvier. Stock limité.
                </div>
                <div className="mt-4 pt-4" style={{ borderTop: "1px solid #d8d1c4" }}>
                  <p className="text-sm mb-3" style={{ color: "#3a2b24", fontFamily: "'Satoshi', sans-serif" }}>Tarifs sur devis — contactez-nous pour obtenir votre tarif personnalisé.</p>
                  <Link to="/contact" className="btn-primary w-full mt-1 py-3 rounded-md text-sm font-body font-500 flex items-center justify-center gap-2" data-testid="rhizomes-devis-cta">
                    Demander un devis <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guide de plantation */}
      <section style={{ backgroundColor: "#eae4d8" }} className="py-16 px-4 sm:px-6 lg:px-8" data-testid="plantation-guide">
        <div className="max-w-7xl mx-auto">
          <div className="section-divider mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl mb-12" style={{ color: "#3a2b24" }}>
            Guide de plantation étape par étape
          </h2>
          <div className="space-y-6">
            {etapes.map((e, i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 rounded-xl"
                style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}
                data-testid={`etape-${i}`}
              >
                <div className="md:col-span-1 flex md:flex-col items-center gap-3">
                  <span className="font-display text-3xl" style={{ color: "#b57a3e" }}>{e.num}</span>
                </div>
                <div className="md:col-span-11">
                  <h3 className="font-display text-xl mb-2" style={{ color: "#3a2b24" }}>{e.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#3a2b24", opacity: 0.75, fontFamily: "'Satoshi', sans-serif" }}>{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
              <h3 className="font-display text-2xl mb-4" style={{ color: "#3a2b24" }}>Conditions favorables</h3>
              {["Sol bien drainé, pH 5,5-7,5", "Température sol > 8°C au moment de la plantation", "Zone sans risque de gel tardif", "Pas d'engorgement hydrique prolongé", "Exposition ensoleillée ou mi-ombre", "Surface minimum 500 m²"].map((item, i) => (
                <div key={i} className="flex items-start gap-2 mb-2 text-sm" style={{ color: "#3a2b24", fontFamily: "'Satoshi', sans-serif" }}>
                  <CheckCircle2 size={16} style={{ color: "#2d5016", marginTop: "2px", flexShrink: 0 }} />
                  {item}
                </div>
              ))}
            </div>
            <div className="p-6 rounded-xl" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
              <h3 className="font-display text-2xl mb-4" style={{ color: "#3a2b24" }}>Conditions à éviter</h3>
              {["Sols asphyxiants ou marécageux", "Zones exposées aux gelées tardives (après mars)", "Zones en creux avec accumulation d'eau", "Sol très calcaire ou très acide", "Zones d'ombre prolongée"].map((item, i) => (
                <div key={i} className="flex items-start gap-2 mb-2 text-sm" style={{ color: "#3a2b24", fontFamily: "'Satoshi', sans-serif" }}>
                  <div className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(181,122,62,0.15)" }}>
                    <AlertTriangle size={10} style={{ color: "#b57a3e" }} />
                  </div>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image du champ */}
      <div className="h-64 sm:h-80 overflow-hidden">
        <img src={IMG_CHAMP} alt="Champ de miscanthus à maturité" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ objectPosition: "center bottom" }} />
      </div>

      {/* CTA */}
      <section style={{ backgroundColor: "#2d5016" }} className="py-12 px-4 text-center">
        <h2 className="font-display text-3xl mb-4" style={{ color: "#f5f2ec" }}>Prêt à vous lancer ?</h2>
        <p className="text-sm mb-6 opacity-75" style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
          Nos experts vous accompagnent dans votre projet de plantation de miscanthus.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className="btn-secondary inline-flex items-center gap-2 px-8 py-3.5 rounded-md font-body font-500 text-base" data-testid="rhizomes-final-cta">
            Demander un devis <ArrowRight size={18} />
          </Link>
          <Link to="/faq" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md text-sm font-body font-500" style={{ backgroundColor: "rgba(245,242,236,0.15)", color: "#f5f2ec", border: "1px solid rgba(245,242,236,0.3)" }}>
            FAQ technique
          </Link>
        </div>
      </section>
    </main>
  );
}
